import { z } from 'zod';

const STORAGE_KEY = 'bb_guest_orders';
const MAX_ENTRIES = 25;

export const guestOrderEntrySchema = z.object({
  orderId: z.string().min(1),
  guestOrderToken: z.string().min(1),
  restaurantId: z.string().min(1),
  storedAt: z.number().int().nonnegative(),
});
export type GuestOrderEntry = z.infer<typeof guestOrderEntrySchema>;

const guestOrderListSchema = z.array(guestOrderEntrySchema);

export interface GuestOrderStorageAdapter {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
}

function parseEntries(raw: string | null): GuestOrderEntry[] {
  if (raw === null) return [];
  try {
    const parsed = guestOrderListSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

export function createGuestOrderStore(storage: GuestOrderStorageAdapter) {
  async function read(): Promise<GuestOrderEntry[]> {
    const raw = await storage.getItem(STORAGE_KEY);
    return parseEntries(raw);
  }

  async function write(entries: GuestOrderEntry[]): Promise<void> {
    try {
      await storage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage may be unavailable; fail silently.
    }
  }

  return {
    async rememberGuestOrder(entry: Omit<GuestOrderEntry, 'storedAt'>): Promise<void> {
      const next: GuestOrderEntry = { ...entry, storedAt: Date.now() };
      const existing = await read();
      const others = existing.filter((e) => e.orderId !== entry.orderId);
      await write([next, ...others].slice(0, MAX_ENTRIES));
    },

    async listRememberedGuestOrders(): Promise<GuestOrderEntry[]> {
      const entries = await read();
      return [...entries].sort((a, b) => b.storedAt - a.storedAt);
    },

    async getStoredGuestOrderToken(orderId: string): Promise<string | undefined> {
      const entries = await read();
      return entries.find((entry) => entry.orderId === orderId)?.guestOrderToken;
    },

    async forgetGuestOrder(orderId: string): Promise<void> {
      const existing = await read();
      await write(existing.filter((entry) => entry.orderId !== orderId));
    },
  };
}

/** Synchronous variant for web (localStorage is synchronous). */
export function createSyncGuestOrderStore(storage: {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}) {
  function read(): GuestOrderEntry[] {
    try {
      return parseEntries(storage.getItem(STORAGE_KEY));
    } catch {
      return [];
    }
  }

  function write(entries: GuestOrderEntry[]): void {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // fail silently
    }
  }

  return {
    rememberGuestOrder(entry: Omit<GuestOrderEntry, 'storedAt'>): void {
      const next: GuestOrderEntry = { ...entry, storedAt: Date.now() };
      const others = read().filter((e) => e.orderId !== entry.orderId);
      write([next, ...others].slice(0, MAX_ENTRIES));
    },

    listRememberedGuestOrders(): GuestOrderEntry[] {
      return read().sort((a, b) => b.storedAt - a.storedAt);
    },

    getStoredGuestOrderToken(orderId: string): string | undefined {
      return read().find((entry) => entry.orderId === orderId)?.guestOrderToken;
    },

    forgetGuestOrder(orderId: string): void {
      write(read().filter((entry) => entry.orderId !== orderId));
    },
  };
}
